import React, { useState } from 'react';
import { usePermissions } from '../../context/PermissionsContext.tsx';
import {
  Search,
  ArrowUpDown,
  Clock,
  CheckCircle2,
  Circle,
  AlertCircle,
  Layers,
  ChevronDown,
  ChevronRight,
  Plus,
  Table as TableIcon,
  Tag,
  DollarSign,
  Calendar,
  Filter,
  X,
  Users,
} from 'lucide-react';
import { Task, TaskStatus, PriorityLevel, canTransitionTask, checkTaskTransition } from '../../../shared/types.ts';
import { PriorityBadge, StatusBadge } from '../common/Badge.tsx';
import { Avatar } from '../common/Avatar.tsx';
import { useAuth } from '../../context/AuthContext.tsx';

interface TaskTableProps {
  tasks: Task[];
  onTaskClick: (task: Task) => void;
  onUpdateTaskStatus: (taskId: string, newStatus: TaskStatus) => void;
  onQuickCreateTask?: (status: TaskStatus, title: string) => void;
  /** Hide the "All Users" filter (My Tasks only ever holds the person's own tasks) */
  hideUserFilter?: boolean;
}

const STATUS_GROUPS: { key: TaskStatus; label: string; color: string; badgeBg: string }[] = [
  { key: 'todo', label: 'To Do', color: '#3b82f6', badgeBg: 'bg-blue-500/10 text-blue-600 dark:text-blue-400' },
  { key: 'in_progress', label: 'In Progress', color: '#f59e0b', badgeBg: 'bg-amber-500/10 text-amber-600 dark:text-amber-400' },
  { key: 'in_review', label: 'In Review', color: '#8b5cf6', badgeBg: 'bg-purple-500/10 text-purple-600 dark:text-purple-400' },
  { key: 'done', label: 'Completed', color: '#10b981', badgeBg: 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400' },
  { key: 'backlog', label: 'Backlog', color: '#6b7280', badgeBg: 'bg-surface-500/10 text-surface-600 dark:text-surface-400' },
];

export const TaskTable: React.FC<TaskTableProps> = ({
  tasks,
  onTaskClick,
  onUpdateTaskStatus,
  onQuickCreateTask,
  hideUserFilter = false,
}) => {
  const { token, user } = useAuth();
  const [filterSearch, setFilterSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState<string>('all');
  const [filterPriority, setFilterPriority] = useState<string>('all');
  const [filterUser, setFilterUser] = useState<string>('all');
  const [groupByStatus, setGroupByStatus] = useState<boolean>(true); // Status grouping
  const [collapsedGroups, setCollapsedGroups] = useState<Record<string, boolean>>({});
  const [sortField, setSortField] = useState<'title' | 'status' | 'priority' | 'dueDate' | 'estimatedHours'>('dueDate');
  const [sortAsc, setSortAsc] = useState(true);

  // Inline Quick Add state per group
  const [addingInGroup, setAddingInGroup] = useState<TaskStatus | null>(null);
  const [newTitleInput, setNewTitleInput] = useState('');

  const isWorkspaceAdmin = user?.role === 'admin';
  const isProjectLead = isWorkspaceAdmin || user?.role === 'team_lead' || user?.role === 'manager';
  const { can } = usePermissions();
  const canCreateTask = can('task.create');
  const isViewer = user?.role === 'viewer' && !can('task.edit');
  const isGuest = user?.role === 'guest' && !can('task.edit');

  const currentUserForPermissions = React.useMemo(() => ({
    id: user?.id || '',
    workspaceRole: isWorkspaceAdmin ? 'ADMIN' : undefined,
    // what a workspace role may review is decided by the role permissions, through `role`
    projectRole: isViewer ? 'VIEWER' : isGuest ? 'GUEST' : 'MEMBER',
    role: user?.role,
  }), [user?.id, user?.role, isWorkspaceAdmin, isViewer, isGuest]);

  const canApproveTask = (t?: Task | null): boolean => {
    if (!t) return false;
    return canTransitionTask(currentUserForPermissions, t, 'COMPLETED');
  };

  const handleStatusChange = (task: Task, targetStatus: TaskStatus) => {
    const check = checkTaskTransition(currentUserForPermissions, task, targetStatus);
    if (!check.allowed) {
      alert(check.error || 'Workflow Gated: You do not have permission to perform this task status transition.');
      return;
    }
    onUpdateTaskStatus(task.id, targetStatus);
  };

  const handleUpdateDueDate = async (taskId: string, newDueDate: string | null) => {
    if (!token) return;
    try {
      const res = await fetch(`/api/tasks/${taskId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
          ...(user?.id ? { 'x-demo-user-id': user.id } : {}),
        },
        body: JSON.stringify({ dueDate: newDueDate || null }),
      });
      if (res.ok) {
        const data = await res.json();
        if (data.task) {
          window.dispatchEvent(new CustomEvent('task-updated', { detail: data.task }));
        }
      }
    } catch (err) {
      console.error('Failed to update task due date:', err);
    }
  };

  const toggleGroupCollapse = (groupKey: string) => {
    setCollapsedGroups((prev) => ({
      ...prev,
      [groupKey]: !prev[groupKey],
    }));
  };

  // Extract distinct users/assignees from tasks
  const availableUsers = React.useMemo(() => {
    const map = new Map<string, { id: string; fullName: string; count: number }>();
    let unassigned = 0;
    tasks.forEach((t) => {
      const taskAssignees = (t.assignees && t.assignees.length > 0)
        ? t.assignees
        : t.assigneeId
        ? [{ id: t.assigneeId, fullName: t.assigneeName || 'Assignee' }]
        : [];

      if (taskAssignees.length === 0) {
        unassigned += 1;
      } else {
        taskAssignees.forEach((a: any) => {
          if (!a || !a.id) return;
          const existing = map.get(a.id);
          if (existing) {
            existing.count += 1;
          } else {
            map.set(a.id, { id: a.id, fullName: a.fullName || 'Member', count: 1 });
          }
        });
      }
    });
    return { list: Array.from(map.values()), unassigned };
  }, [tasks]);

  const filteredTasks = tasks.filter((t) => {
    if (filterStatus !== 'all' && t.status !== filterStatus) return false;
    if (filterPriority !== 'all' && t.priority !== filterPriority) return false;
    if (filterUser !== 'all') {
      if (filterUser === '__unassigned__') {
        const hasAssignee = Boolean(t.assigneeId || (t.assignees && t.assignees.length > 0));
        if (hasAssignee) return false;
      } else {
        const isAssigned =
          t.assigneeId === filterUser ||
          (t.assignees && t.assignees.some((a) => a.id === filterUser));
        if (!isAssigned) return false;
      }
    }
    if (filterSearch.trim()) {
      const match =
        t.title.toLowerCase().includes(filterSearch.toLowerCase()) ||
        (t.assigneeName && t.assigneeName.toLowerCase().includes(filterSearch.toLowerCase())) ||
        (t.tags && t.tags.some((tag) => tag.toLowerCase().includes(filterSearch.toLowerCase())));
      if (!match) return false;
    }
    return true;
  });

  filteredTasks.sort((a, b) => {
    let cmp = 0;
    if (sortField === 'title') {
      cmp = a.title.localeCompare(b.title);
    } else if (sortField === 'status') {
      cmp = a.status.localeCompare(b.status);
    } else if (sortField === 'priority') {
      const pWeights = { urgent: 4, high: 3, medium: 2, low: 1 };
      cmp = (pWeights[a.priority] || 0) - (pWeights[b.priority] || 0);
    } else if (sortField === 'dueDate') {
      cmp = (a.dueDate || '9999').localeCompare(b.dueDate || '9999');
    } else if (sortField === 'estimatedHours') {
      cmp = (a.estimatedHours || 0) - (b.estimatedHours || 0);
    }
    return sortAsc ? cmp : -cmp;
  });

  const toggleSort = (field: 'title' | 'status' | 'priority' | 'dueDate' | 'estimatedHours') => {
    if (sortField === field) {
      setSortAsc(!sortAsc);
    } else {
      setSortField(field);
      setSortAsc(true);
    }
  };

  const isOverdue = (dateStr?: string | null) => {
    if (!dateStr) return false;
    return dateStr < new Date().toISOString().split('T')[0];
  };

  const handleQuickAddSubmit = (status: TaskStatus) => {
    if (!newTitleInput.trim()) {
      setAddingInGroup(null);
      return;
    }
    if (onQuickCreateTask) {
      onQuickCreateTask(status, newTitleInput.trim());
    }
    setNewTitleInput('');
    setAddingInGroup(null);
  };

  const renderTaskRows = (taskList: Task[]) => {
    return taskList.map((task) => {
      const overdue = isOverdue(task.dueDate) && task.status !== 'done';

      return (
        <tr
          key={task.id}
          onClick={() => onTaskClick(task)}
          className="hover:bg-surface-50 dark:hover:bg-surface-800/40 cursor-pointer transition-colors group border-b border-surface-100 dark:border-surface-800/50"
        >
          {/* Quick Complete Button */}
          <td className="py-2.5 px-3 w-8" onClick={(e) => e.stopPropagation()}>
            <button
              onClick={() =>
                handleStatusChange(task, task.status === 'done' ? 'in_progress' : 'done')
              }
              title={task.status === 'done' ? 'Reopen task' : 'Mark as completed'}
              className="text-surface-400 hover:text-emerald-500 transition-colors"
            >
              {task.status === 'done' ? (
                <CheckCircle2 className="w-4 h-4 text-emerald-500" />
              ) : (
                <Circle className="w-4 h-4" />
              )}
            </button>
          </td>

          {/* Title & Tags */}
          <td className="py-2.5 px-3 min-w-[200px]">
            <div className="flex items-center space-x-2">
              {task.code && (
                <span className="text-[10px] font-mono font-bold px-1.5 py-0.5 rounded bg-surface-100 dark:bg-surface-800 text-surface-600 dark:text-surface-300 border border-surface-200 dark:border-surface-700 shrink-0">
                  {task.code}
                </span>
              )}
              <div className="font-semibold text-xs text-surface-900 dark:text-surface-100 group-hover:text-brand-600 dark:group-hover:text-brand-400 leading-tight">
                {task.title}
              </div>
            </div>
            {task.tags && task.tags.length > 0 && (
              <div className="flex flex-wrap items-center gap-1 mt-1">
                {task.tags.map((t) => (
                  <span
                    key={t}
                    className="px-1.5 py-0.2 text-[9px] font-medium rounded-md bg-surface-100 dark:bg-surface-800 text-surface-600 dark:text-surface-400 border border-surface-200 dark:border-surface-700/50"
                  >
                    #{t}
                  </span>
                ))}
              </div>
            )}
          </td>

          {/* Status Dropdown */}
          <td className="py-2.5 px-3 w-32" onClick={(e) => e.stopPropagation()}>
            <select
              value={task.status}
              disabled={isViewer || isGuest}
              onChange={(e) => handleStatusChange(task, e.target.value as TaskStatus)}
              aria-label="Update task status"
              className="text-xs bg-surface-50 dark:bg-surface-800/70 border border-surface-200 dark:border-surface-700 rounded-lg px-2 py-1 font-semibold text-surface-700 dark:text-surface-300 cursor-pointer focus:ring-1 focus:ring-brand-500 disabled:opacity-50"
            >
              <option value="backlog">Backlog</option>
              <option value="todo">To Do</option>
              <option value="in_progress">In Progress</option>
              <option value="in_review">In Review</option>
              <option value="done" disabled={!canApproveTask(task)}>
                Completed {!canApproveTask(task) ? '(Locked)' : ''}
              </option>
            </select>
          </td>

          {/* Priority */}
          <td className="py-2.5 px-3 w-28">
            <PriorityBadge priority={task.priority} size="sm" />
          </td>

          {/* Assignee */}
          <td className="py-2.5 px-3 w-36">
            <div className="flex items-center space-x-2">
              <Avatar
                src={task.assigneeAvatar}
                name={task.assigneeName || 'Unassigned'}
                size="xs"
              />
              <span className="text-xs text-surface-700 dark:text-surface-300 truncate max-w-[100px] font-medium">
                {task.assigneeName || 'Unassigned'}
              </span>
            </div>
          </td>

          {/* Due Date (Inline Editable) */}
          <td className="py-2.5 px-3 w-36" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center space-x-1">
              {overdue && <AlertCircle className="w-3.5 h-3.5 text-rose-500 shrink-0" />}
              <input
                type="date"
                value={task.dueDate ? String(task.dueDate).slice(0, 10) : ''}
                onChange={(e) => handleUpdateDueDate(task.id, e.target.value || null)}
                aria-label="Edit due date"
                className={`text-xs bg-transparent hover:bg-surface-100 dark:hover:bg-surface-800 border border-transparent hover:border-surface-200 dark:hover:border-surface-700 rounded-lg px-1.5 py-1 cursor-pointer focus:outline-none focus:ring-1 focus:ring-brand-500 ${
                  overdue
                    ? 'text-rose-600 dark:text-rose-400 font-bold'
                    : task.dueDate
                    ? 'text-surface-700 dark:text-surface-300 font-medium'
                    : 'text-surface-400'
                }`}
              />
            </div>
          </td>

          {/* Estimate / Hours */}
          <td className="py-2.5 px-3 w-24 text-right">
            <span className="text-xs font-mono font-semibold text-surface-600 dark:text-surface-400">
              {task.estimatedHours ? `${task.estimatedHours}h` : '—'}
            </span>
          </td>
        </tr>
      );
    });
  };

  const [mobileViewMode, setMobileViewMode] = useState<'cards' | 'table'>('cards');

  const renderTaskCards = (taskList: Task[]) => {
    return taskList.map((task) => {
      const overdue = isOverdue(task.dueDate) && task.status !== 'done';

      return (
        <div
          key={task.id}
          onClick={() => onTaskClick(task)}
          className="p-3.5 bg-white dark:bg-surface-900 border-b border-surface-100 dark:border-surface-800 flex flex-col space-y-2 active:bg-surface-50 dark:active:bg-surface-800/40 transition-colors"
        >
          <div className="flex items-start justify-between gap-2">
            <div className="flex items-start space-x-2.5 min-w-0">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleStatusChange(task, task.status === 'done' ? 'in_progress' : 'done');
                }}
                className="mt-0.5 text-surface-400 hover:text-emerald-500 shrink-0 p-1 -m-1"
                aria-label={task.status === 'done' ? 'Reopen task' : 'Mark completed'}
              >
                {task.status === 'done' ? (
                  <CheckCircle2 className="w-4 h-4 text-emerald-500" />
                ) : (
                  <Circle className="w-4 h-4" />
                )}
              </button>
              <div className="min-w-0">
                <div className="flex items-center space-x-1.5 flex-wrap gap-y-0.5 mb-1">
                  {task.code && (
                    <span className="text-[10px] font-mono font-bold px-1.5 py-0.2 rounded bg-surface-100 dark:bg-surface-800 text-surface-600 dark:text-surface-300 border border-surface-200 dark:border-surface-700">
                      {task.code}
                    </span>
                  )}
                  <PriorityBadge priority={task.priority} size="sm" />
                </div>
                <h4 className="font-semibold text-xs text-surface-900 dark:text-surface-100 leading-snug">
                  {task.title}
                </h4>
              </div>
            </div>

            {/* Status selector */}
            <div onClick={(e) => e.stopPropagation()} className="shrink-0">
              <select
                value={task.status}
                disabled={isViewer || isGuest}
                onChange={(e) => handleStatusChange(task, e.target.value as TaskStatus)}
                aria-label="Update task status"
                className="text-[11px] bg-surface-100 dark:bg-surface-800 border border-surface-200 dark:border-surface-700 rounded-lg px-2 py-1 font-semibold text-surface-700 dark:text-surface-300 cursor-pointer min-h-[30px] disabled:opacity-50"
              >
                <option value="backlog">Backlog</option>
                <option value="todo">To Do</option>
                <option value="in_progress">In Progress</option>
                <option value="in_review">In Review</option>
                <option value="done" disabled={!canApproveTask(task)}>
                  Completed {!canApproveTask(task) ? '(Locked)' : ''}
                </option>
              </select>
            </div>
          </div>

          {/* Tags */}
          {task.tags && task.tags.length > 0 && (
            <div className="flex flex-wrap items-center gap-1 pl-6">
              {task.tags.map((t) => (
                <span
                  key={t}
                  className="px-1.5 py-0.2 text-[9px] font-medium rounded-md bg-surface-100 dark:bg-surface-800 text-surface-600 dark:text-surface-400 border border-surface-200 dark:border-surface-700/50"
                >
                  #{t}
                </span>
              ))}
            </div>
          )}

          {/* Card Bottom Meta */}
          <div className="flex items-center justify-between text-[11px] pt-1.5 border-t border-surface-100 dark:border-surface-800/60 text-surface-500 pl-6">
            <div className="flex items-center space-x-1.5">
              <Avatar src={task.assigneeAvatar} name={task.assigneeName || 'Unassigned'} size="xs" />
              <span className="truncate max-w-[120px] font-medium">{task.assigneeName || 'Unassigned'}</span>
            </div>

            <div className="flex items-center space-x-2.5" onClick={(e) => e.stopPropagation()}>
              <div className="flex items-center">
                {overdue && <AlertCircle className="w-3 h-3 mr-1 text-rose-500 shrink-0" />}
                <input
                  type="date"
                  value={task.dueDate ? String(task.dueDate).slice(0, 10) : ''}
                  onChange={(e) => handleUpdateDueDate(task.id, e.target.value || null)}
                  aria-label="Edit due date"
                  className={`text-[11px] bg-transparent border border-transparent hover:border-surface-200 dark:hover:border-surface-700 rounded px-1 py-0.5 cursor-pointer ${
                    overdue ? 'text-rose-600 dark:text-rose-400 font-bold' : 'text-surface-600 dark:text-surface-400'
                  }`}
                />
              </div>
              {task.estimatedHours ? (
                <span className="font-mono text-[10px] bg-surface-100 dark:bg-surface-800 px-1.5 py-0.5 rounded text-surface-600 dark:text-surface-400">
                  {task.estimatedHours}h
                </span>
              ) : null}
            </div>
          </div>
        </div>
      );
    });
  };

  return (
    <div className="bg-white dark:bg-surface-900 rounded-3xl shadow-neo-card border border-[#d5d8de]/60 dark:border-surface-800 overflow-hidden font-sans">
      {/* Table Toolbar & Dense View Controls */}
      <div className="p-3.5 sm:p-4 border-b border-surface-200 dark:border-surface-800 flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap items-center gap-2.5 sm:gap-3 w-full sm:w-auto">
          {/* Quick Search */}
          <div className="relative w-full sm:w-64">
            <Search className="w-3.5 h-3.5 text-surface-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <input
              type="text"
              placeholder="Search tasks, assignees, tags..."
              value={filterSearch}
              onChange={(e) => setFilterSearch(e.target.value)}
              className="w-full pl-8 pr-3 py-1.5 text-xs rounded-xl bg-surface-50 dark:bg-surface-800 border border-surface-200 dark:border-surface-700 text-surface-900 dark:text-surface-100 placeholder-surface-400 focus:outline-none focus:ring-1 focus:ring-brand-500"
            />
          </div>

          <div className="flex items-center space-x-2">
            {/* Group by Status Toggle */}
            <button
              onClick={() => setGroupByStatus(!groupByStatus)}
              className={`px-3 py-1.5 rounded-xl text-xs font-bold flex items-center space-x-1.5 transition-all ${
                groupByStatus
                  ? 'bg-[#121417] text-white dark:bg-white dark:text-black shadow-sm'
                  : 'bg-surface-100 dark:bg-surface-800 text-surface-600 dark:text-surface-400 hover:bg-surface-200'
              }`}
              title="Group rows by Status"
            >
              <Layers className="w-3.5 h-3.5" />
              <span>Group: {groupByStatus ? 'Status' : 'Flat'}</span>
            </button>

            {/* Mobile Cards / Table Switcher */}
            <button
              onClick={() => setMobileViewMode(mobileViewMode === 'cards' ? 'table' : 'cards')}
              className="sm:hidden px-2.5 py-1.5 rounded-xl text-xs font-bold flex items-center space-x-1 bg-surface-100 dark:bg-surface-800 text-surface-700 dark:text-surface-300 border border-surface-200 dark:border-surface-700"
              title="Toggle between Card and Table view on mobile"
            >
              <TableIcon className="w-3.5 h-3.5" />
              <span>{mobileViewMode === 'cards' ? 'Cards' : 'Table'}</span>
            </button>
          </div>
        </div>

        {/* Filter dropdowns */}
        <div className="flex flex-wrap items-center gap-2">
          {/* User-Wise Filter */}
          <div className={`flex items-center space-x-1.5 px-2.5 py-1 rounded-xl bg-surface-50 dark:bg-surface-800 border border-surface-200 dark:border-surface-700 ${hideUserFilter ? 'hidden' : ''}`}>
            <Users className="w-3.5 h-3.5 text-surface-400 shrink-0" />
            <select
              value={filterUser}
              onChange={(e) => setFilterUser(e.target.value)}
              aria-label="Filter by User"
              className="text-xs bg-transparent text-surface-700 dark:text-surface-300 focus:outline-none font-medium w-[140px] truncate cursor-pointer"
            >
              <option value="all">All Users ({availableUsers.list.length})</option>
              {user && (
                <option value={user.id}>
                  Assigned to Me ({tasks.filter((t) => t.assigneeId === user.id || (t.assignees && t.assignees.some((a) => a.id === user.id))).length})
                </option>
              )}
              {availableUsers.unassigned > 0 && (
                <option value="__unassigned__">
                  Unassigned ({availableUsers.unassigned})
                </option>
              )}
              {availableUsers.list
                .filter((u) => !user || u.id !== user.id)
                .map((u) => (
                  <option key={u.id} value={u.id}>
                    {u.fullName} ({u.count})
                  </option>
                ))}
            </select>
            {filterUser !== 'all' && (
              <button
                type="button"
                onClick={() => setFilterUser('all')}
                className="text-surface-400 hover:text-surface-600 dark:hover:text-surface-200"
                title="Clear user filter"
              >
                <X className="w-3 h-3" />
              </button>
            )}
          </div>

          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            aria-label="Filter by Status"
            className="text-xs px-2.5 py-1.5 rounded-xl bg-surface-50 dark:bg-surface-800 border border-surface-200 dark:border-surface-700 text-surface-700 dark:text-surface-300 focus:outline-none font-medium"
          >
            <option value="all">All Statuses</option>
            <option value="todo">To Do</option>
            <option value="in_progress">In Progress</option>
            <option value="in_review">In Review</option>
            <option value="done">Completed</option>
            <option value="backlog">Backlog</option>
          </select>

          <select
            value={filterPriority}
            onChange={(e) => setFilterPriority(e.target.value)}
            aria-label="Filter by Priority"
            className="text-xs px-2.5 py-1.5 rounded-xl bg-surface-50 dark:bg-surface-800 border border-surface-200 dark:border-surface-700 text-surface-700 dark:text-surface-300 focus:outline-none font-medium"
          >
            <option value="all">All Priorities</option>
            <option value="urgent">Urgent</option>
            <option value="high">High</option>
            <option value="medium">Medium</option>
            <option value="low">Low</option>
          </select>
        </div>
      </div>

      {/* Mobile-Friendly Native Task Cards View */}
      {mobileViewMode === 'cards' && (
        <div className="sm:hidden">
          {filteredTasks.length === 0 ? (
            <div className="py-12 text-center text-surface-400 text-xs">
              No tasks found matching your filters.
            </div>
          ) : groupByStatus ? (
            STATUS_GROUPS.map((group) => {
              const groupTasks = filteredTasks.filter((t) => t.status === group.key);
              const isCollapsed = !!collapsedGroups[group.key];
              if (groupTasks.length === 0 && filterStatus !== 'all') return null;

              return (
                <div key={group.key} className="border-b border-surface-200 dark:border-surface-800">
                  <div className="bg-[#f1f3f5] dark:bg-surface-800/60 p-2.5 flex items-center justify-between">
                    <button
                      onClick={() => toggleGroupCollapse(group.key)}
                      className="flex items-center space-x-2 text-xs font-black text-surface-800 dark:text-surface-200"
                    >
                      {isCollapsed ? (
                        <ChevronRight className="w-3.5 h-3.5 text-surface-500" />
                      ) : (
                        <ChevronDown className="w-3.5 h-3.5 text-surface-500" />
                      )}
                      <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: group.color }} />
                      <span>{group.label}</span>
                      <span className="px-1.5 py-0.2 rounded-full text-[10px] bg-white dark:bg-surface-900 text-surface-600 dark:text-surface-400 font-bold border border-surface-200 dark:border-surface-700">
                        {groupTasks.length}
                      </span>
                    </button>

                    {canCreateTask && onQuickCreateTask && (
                      <button
                        onClick={() => setAddingInGroup(group.key)}
                        className="text-[11px] font-bold text-surface-600 dark:text-surface-400 flex items-center space-x-1"
                      >
                        <Plus className="w-3 h-3" />
                        <span>Add</span>
                      </button>
                    )}
                  </div>

                  {!isCollapsed && addingInGroup === group.key && (
                    <div className="p-3 bg-brand-50/40 dark:bg-brand-950/20 border-b border-brand-500/20 flex items-center space-x-2">
                      <input
                        type="text"
                        autoFocus
                        placeholder={`New task for ${group.label}...`}
                        value={newTitleInput}
                        onChange={(e) => setNewTitleInput(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') handleQuickAddSubmit(group.key);
                          if (e.key === 'Escape') setAddingInGroup(null);
                        }}
                        className="w-full bg-white dark:bg-surface-900 border border-brand-500/40 rounded-lg px-2.5 py-1.5 text-xs text-surface-900 dark:text-surface-100 focus:outline-none"
                      />
                      <button
                        onClick={() => handleQuickAddSubmit(group.key)}
                        className="px-3 py-1.5 rounded-lg bg-pulse-gradient text-white text-xs font-bold shrink-0"
                      >
                        Add
                      </button>
                      <button
                        onClick={() => setAddingInGroup(null)}
                        className="p-1.5 text-surface-500"
                      >
                        <X className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  )}

                  {!isCollapsed && renderTaskCards(groupTasks)}
                </div>
              );
            })
          ) : (
            renderTaskCards(filteredTasks)
          )}
        </div>
      )}

      {/* Spreadsheet / Table Grid View (Desktop Persistent + Mobile Optional) */}
      <div
        onWheel={(e) => {
          if (Math.abs(e.deltaX) > Math.abs(e.deltaY) || e.deltaY === 0) return;
          // Sideways only with Shift held; reaching the bottom must not scroll the table sideways
          const el = e.currentTarget;
          const canScrollHorizontally =
            (e.deltaY > 0 && el.scrollLeft + el.clientWidth < el.scrollWidth - 2) ||
            (e.deltaY < 0 && el.scrollLeft > 1);

          if (e.shiftKey && canScrollHorizontally) {
            e.preventDefault();
            el.scrollLeft += e.deltaY;
          }
        }}
        className={`overflow-auto max-h-[calc(100vh-14.5rem)] touch-scroll ${
          mobileViewMode === 'cards' ? 'hidden sm:block' : 'block'
        }`}
      >
        <table className="w-full min-w-[780px] text-left text-xs border-collapse">
          <thead className="bg-[#f8f9fa] dark:bg-surface-800 border-b border-surface-200 dark:border-surface-800 uppercase tracking-wider text-[10px] font-bold text-surface-500 sticky top-0 z-10 shadow-2xs">
            <tr>
              <th className="py-2.5 px-3 w-8"></th>
              <th
                onClick={() => toggleSort('title')}
                className="py-2.5 px-3 cursor-pointer hover:text-surface-900 dark:hover:text-surface-100 min-w-[200px]"
              >
                <div className="flex items-center space-x-1">
                  <span>Task Title</span>
                  <ArrowUpDown className="w-3 h-3" />
                </div>
              </th>
              <th
                onClick={() => toggleSort('status')}
                className="py-2.5 px-3 cursor-pointer hover:text-surface-900 dark:hover:text-surface-100 w-32"
              >
                <div className="flex items-center space-x-1">
                  <span>Status</span>
                  <ArrowUpDown className="w-3 h-3" />
                </div>
              </th>
              <th
                onClick={() => toggleSort('priority')}
                className="py-2.5 px-3 cursor-pointer hover:text-surface-900 dark:hover:text-surface-100 w-28"
              >
                <div className="flex items-center space-x-1">
                  <span>Priority</span>
                  <ArrowUpDown className="w-3 h-3" />
                </div>
              </th>
              <th className="py-2.5 px-3 w-36">Assignee</th>
              <th
                onClick={() => toggleSort('dueDate')}
                className="py-2.5 px-3 cursor-pointer hover:text-surface-900 dark:hover:text-surface-100 w-32"
              >
                <div className="flex items-center space-x-1">
                  <span>Due Date</span>
                  <ArrowUpDown className="w-3 h-3" />
                </div>
              </th>
              <th
                onClick={() => toggleSort('estimatedHours')}
                className="py-2.5 px-3 cursor-pointer hover:text-surface-900 dark:hover:text-surface-100 w-24 text-right"
              >
                <div className="flex items-center justify-end space-x-1">
                  <span>Est. Hours</span>
                  <ArrowUpDown className="w-3 h-3" />
                </div>
              </th>
            </tr>
          </thead>

          <tbody>
            {filteredTasks.length === 0 ? (
              <tr>
                <td colSpan={7} className="py-12 text-center text-surface-400 text-xs">
                  No tasks found matching your filters.
                </td>
              </tr>
            ) : groupByStatus ? (
              // Status Grouped Layout (Spreadsheet Sections)
              STATUS_GROUPS.map((group) => {
                const groupTasks = filteredTasks.filter((t) => t.status === group.key);
                const isCollapsed = !!collapsedGroups[group.key];

                if (groupTasks.length === 0 && filterStatus !== 'all') return null;

                return (
                  <React.Fragment key={group.key}>
                    {/* Status Group Header Row */}
                    <tr className="bg-[#f1f3f5] dark:bg-surface-800/50 border-y border-surface-200 dark:border-surface-700">
                      <td colSpan={7} className="py-2 px-3">
                        <div className="flex items-center justify-between">
                          <button
                            onClick={() => toggleGroupCollapse(group.key)}
                            className="flex items-center space-x-2 text-xs font-black text-surface-800 dark:text-surface-200 hover:text-black dark:hover:text-white"
                          >
                            {isCollapsed ? (
                              <ChevronRight className="w-3.5 h-3.5 text-surface-500" />
                            ) : (
                              <ChevronDown className="w-3.5 h-3.5 text-surface-500" />
                            )}
                            <span
                              className="w-2.5 h-2.5 rounded-full"
                              style={{ backgroundColor: group.color }}
                            />
                            <span>{group.label}</span>
                            <span className="px-2 py-0.5 rounded-full text-[10px] bg-white dark:bg-surface-900 text-surface-600 dark:text-surface-400 font-bold border border-surface-200 dark:border-surface-700">
                              {groupTasks.length}
                            </span>
                          </button>

                          {/* Quick inline Add Task to this status */}
                          {canCreateTask && onQuickCreateTask && (
                            <button
                              onClick={() => setAddingInGroup(group.key)}
                              className="text-[11px] font-bold text-surface-600 dark:text-surface-400 hover:text-black dark:hover:text-white flex items-center space-x-1"
                            >
                              <Plus className="w-3 h-3" />
                              <span>Add row</span>
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>

                    {/* Group Items */}
                    {!isCollapsed && renderTaskRows(groupTasks)}

                    {/* Inline Quick Add Input Row */}
                    {!isCollapsed && addingInGroup === group.key && (
                      <tr className="bg-brand-50/40 dark:bg-brand-950/20 border-b border-brand-500/20">
                        <td className="py-2 px-3 text-center">
                          <Plus className="w-3.5 h-3.5 text-brand-600 dark:text-brand-400" />
                        </td>
                        <td colSpan={6} className="py-2 px-3">
                          <div className="flex items-center space-x-2">
                            <input
                              type="text"
                              autoFocus
                              placeholder={`Type new task title for ${group.label} and press Enter...`}
                              value={newTitleInput}
                              onChange={(e) => setNewTitleInput(e.target.value)}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') handleQuickAddSubmit(group.key);
                                if (e.key === 'Escape') setAddingInGroup(null);
                              }}
                              className="w-full bg-white dark:bg-surface-900 border border-brand-500/40 rounded-lg px-2.5 py-1 text-xs text-surface-900 dark:text-surface-100 focus:outline-none"
                            />
                            <button
                              onClick={() => handleQuickAddSubmit(group.key)}
                              className="px-3 py-1 rounded-lg bg-pulse-gradient hover:opacity-95 text-white text-xs font-bold shrink-0 shadow-sm transition-all"
                            >
                              Add
                            </button>
                            <button
                              onClick={() => setAddingInGroup(null)}
                              className="px-2 py-1 text-xs text-surface-500 hover:text-surface-700 shrink-0"
                            >
                              Cancel
                            </button>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })
            ) : (
              // Flat Table View
              renderTaskRows(filteredTasks)
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};
